import { useEffect, useState } from "react";

/**
 * Track which section heading is currently "active" as the reader scrolls, for a
 * premium docs-site table of contents.
 *
 * Uses a single IntersectionObserver watching every section element by id; the
 * active id is the topmost section currently intersecting the upper band of the
 * viewport. A `rootMargin` that pulls the bottom boundary far up means a heading
 * becomes active as it reaches the top quarter of the screen (so the TOC
 * highlight tracks reading position, not just whatever is barely on-screen).
 * Source: https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver
 *
 * @param ids ordered list of section element ids to observe
 * @returns the id of the active section, or the first id before any intersect
 */
export function useScrollSpy(ids: ReadonlyArray<string>): string {
	const [activeId, setActiveId] = useState<string>(ids[0] ?? "");

	// Callers build a fresh `ids` array every render, so depending on `ids`
	// directly would re-run the effect on EVERY render — tearing down and
	// recreating the observer and re-anchoring `activeId` back to the first id, so
	// the highlight never tracks past the first section. Key on the contents
	// instead, so the effect only re-runs when the set of ids actually changes
	// (e.g. navigating between doc pages). Ids are url-slugs, so "\n" is a safe join.
	const idsKey = ids.join("\n");

	useEffect(() => {
		const idList = idsKey ? idsKey.split("\n") : [];
		if (idList.length === 0) {
			// No tracked headings on this page — clear any stale highlight left over
			// from a previous page rather than returning with the old active id.
			setActiveId("");
			return;
		}

		// Re-anchor when the id set changes (e.g. navigating between doc pages).
		setActiveId(idList[0] ?? "");

		const elements = idList.map((id) => document.getElementById(id)).filter((el): el is HTMLElement => el !== null);

		if (elements.length === 0) {
			return;
		}

		// Track intersection ratios so we can pick the topmost visible section
		// deterministically rather than relying on observer callback order.
		const visibility = new Map<string, boolean>();

		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					visibility.set(entry.target.id, entry.isIntersecting);
				}
				// Active = first id (in document order) currently intersecting.
				const firstVisible = idList.find((id) => visibility.get(id));
				if (firstVisible) {
					setActiveId(firstVisible);
				}
			},
			{
				// Activate when a heading reaches the top ~25% of the viewport, and
				// stop counting it once it scrolls past the top ~10%.
				rootMargin: "-10% 0px -75% 0px",
				threshold: 0,
			},
		);

		for (const el of elements) {
			observer.observe(el);
		}

		return () => observer.disconnect();
	}, [idsKey]);

	return activeId;
}
